/**
 * Karishma's Off-System Booking Portal — Release 28 (2026-04-30)
 * Working version: book a package, see bookings + earnings, propose driver/vehicle.
 */
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import {
  ClipboardList, LogOut, Plus, KeyRound, Truck, UserPlus, CheckCircle2,
  AlertCircle, Loader2, BarChart3, FileText, MapPin, Mail, MessageCircle,
  Copy as CopyIcon, X as XIcon, Navigation, Search, UserSearch,
} from "lucide-react";

const fmtJMD = (n) =>
  `JM$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PARISH_FALLBACK = [
  "Kingston", "St. Andrew", "St. Catherine", "Clarendon",
  "Manchester", "St. Elizabeth", "Westmoreland", "Hanover",
  "St. James", "Trelawny", "St. Ann", "St. Mary",
  "Portland", "St. Thomas",
];

export default function KarishmaPortal() {
  const navigate = useNavigate();
  const [parishes, setParishes] = useState(PARISH_FALLBACK);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [summary, setSummary] = useState(null);
  // R29: vehicle ops forms (expense + income)
  const [vexpType, setVexpType] = useState("fuel");
  const [vexpAmount, setVexpAmount] = useState("");
  const [vexpNote, setVexpNote] = useState("");
  const [vexpSubmitting, setVexpSubmitting] = useState(false);
  const [vincAmount, setVincAmount] = useState("");
  const [vincDescription, setVincDescription] = useState("");
  const [vincCustomer, setVincCustomer] = useState("");
  const [vincSubmitting, setVincSubmitting] = useState(false);
  // C1: filter state (frontend-only)
  const [filterMonth, setFilterMonth] = useState("");
  const [filterParish, setFilterParish] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  // C2: withdrawal state
  const [c2Balance, setC2Balance] = useState(null);
  const [c2History, setC2History] = useState([]);
  const [c2Show, setC2Show] = useState(false);
  const [c2Amount, setC2Amount] = useState("");
  const [c2Notes, setC2Notes] = useState("");
  const [c2Submitting, setC2Submitting] = useState(false);
  const c2Reload = React.useCallback(async () => {
    try {
      const [b, h] = await Promise.all([
        api.get("/karishma/withdrawals/balance"),
        api.get("/karishma/withdrawals?limit=20"),
      ]);
      setC2Balance(b.data || null);
      setC2History(h.data?.withdrawals || []);
    } catch (e) { /* silent */ }
  }, []);
  React.useEffect(() => { c2Reload(); }, [c2Reload]);

  // C1: derive filter option lists + filtered list (frontend-only, no extra API calls)
  const monthOptions = React.useMemo(() => {
    const set = new Set();
    (bookings || []).forEach((b) => {
      const ym = (b.created_at || "").slice(0, 7);
      if (ym) set.add(ym);
    });
    return Array.from(set).sort().reverse();
  }, [bookings]);
  const parishOptions = React.useMemo(() => {
    const set = new Set();
    (bookings || []).forEach((b) => { if (b.parish) set.add(b.parish); });
    return Array.from(set).sort();
  }, [bookings]);
  const statusOptions = React.useMemo(() => {
    const set = new Set();
    (bookings || []).forEach((b) => { if (b.status) set.add(b.status); });
    return Array.from(set).sort();
  }, [bookings]);
  // R30 B3: address autocomplete sources — derived from past bookings (frontend-only)
  const pickupAddressSuggestions = React.useMemo(() => {
    const set = new Set();
    (bookings || []).forEach((b) => {
      const a = (b.client_address || "").trim();
      if (a && a.length >= 3) set.add(a);
    });
    return Array.from(set).sort();
  }, [bookings]);
  const destinationSuggestions = React.useMemo(() => {
    const set = new Set();
    (bookings || []).forEach((b) => {
      const d = (b.destination || "").trim();
      if (d && d.length >= 3) set.add(d);
    });
    return Array.from(set).sort();
  }, [bookings]);
  const filteredBookings = React.useMemo(() => {
    const q = (searchTerm || "").trim().toLowerCase();
    return (bookings || []).filter((b) => {
      if (filterMonth && (b.created_at || "").slice(0, 7) !== filterMonth) return false;
      if (filterParish && b.parish !== filterParish) return false;
      if (filterStatus && b.status !== filterStatus) return false;
      if (q) {
        const hay = [b.client_name, b.client_phone, b.id, b.tracking_id]
          .filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [bookings, filterMonth, filterParish, filterStatus, searchTerm]);
  const [activeForm, setActiveForm] = useState(null); // null | 'book' | 'driver' | 'vehicle'
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  // R30 B2: Karishma -> Customer GPS share dialog state
  // R30 B6.2 -- payment link panel state
  const [payBooking, setPayBooking] = useState(null);   // booking row being paid
  const [payCurrency, setPayCurrency] = useState("JMD");
  const [payQuote, setPayQuote] = useState(null);       // computed quote preview
  const [payResult, setPayResult] = useState(null);     // {pay_url, channels, ...}
  const [paySubmitting, setPaySubmitting] = useState(false);
  const [payCopyOk, setPayCopyOk] = useState(false);
  const [gpsBooking, setGpsBooking] = useState(null);   // booking row being shared
  const [gpsShare, setGpsShare] = useState(null);       // {token, share_url, expires_at}
  const [gpsBusy, setGpsBusy] = useState(false);
  const [gpsErr, setGpsErr] = useState("");
  const [gpsEmailOpen, setGpsEmailOpen] = useState(false);
  const [gpsEmailTo, setGpsEmailTo] = useState("");
  const [gpsCopied, setGpsCopied] = useState(false);
  const [gpsEmailSent, setGpsEmailSent] = useState(false);
  // R30 B6-cust-merged: customer auto-suggest tied to the live name/phone inputs
  const [custFocus, setCustFocus] = useState(null); // 'name' | 'phone' | null
  const [custResults, setCustResults] = useState([]);
  const [custBusy, setCustBusy] = useState(false);
  // R30 B4: Karishma starting-point state
  const [startingPoint, setStartingPoint] = useState(null);
  const [spOpen, setSpOpen] = useState(false);
  const [spForm, setSpForm] = useState({});
  const [spBusy, setSpBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const [b, s, f, p, sp] = await Promise.all([
        api.get("/karishma/bookings?limit=50"),
        api.get("/karishma/summary"),
        api.get("/karishma/fleet"),
        api.get("/karishma/parishes"),
        api.get("/karishma/starting-point").catch(() => ({ data: { starting_point: null } })),
      ]);
      setBookings(b.data?.bookings || []);
      setSummary(s.data || null);
      setDrivers(f.data?.drivers || []);
      setVehicles(f.data?.vehicles || []);
      if (p.data?.parishes?.length) setParishes(p.data.parishes);
      setStartingPoint(sp.data?.starting_point || null);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        navigate("/pin");
      }
    }
  }, [navigate]);

  // Hotfix: pre-flight token guard so we bounce to PIN (light theme) instead of falling
  // through the api.js 401 interceptor which sends to /login (dark theme = "black screen").
  useEffect(() => {
    if (!localStorage.getItem("token")) { navigate("/pin?label=karishma"); return; }
    reload();
  }, [reload, navigate]);

  const showToast = (kind, text) => {
    setToast({ kind, text });
    setTimeout(() => setToast(null), 3500);
  };

  const handleSignOut = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userId");
    navigate("/pin");
  };

  const openForm = (which) => {
    setForm({});
    setActiveForm(which);
  };
  const closeForm = () => { setActiveForm(null); setForm({}); };

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  // R30 B2: poll the share token so captured customer coords flow back into the
  // dialog (and into form state for the in-form variant) without manual refresh.
  useEffect(() => {
    if (!gpsShare?.token) return undefined;
    if (gpsShare?.location) return undefined; // already submitted
    let stopped = false;
    const tick = async () => {
      try {
        const r = await api.get(`/karishma/gps-share/poll/${gpsShare.token}`);
        if (stopped) return;
        if (r.data?.status === "submitted" && r.data?.location) {
          setGpsShare((prev) => prev ? { ...prev, location: r.data.location, status: "submitted" } : prev);
          if (gpsBooking?._inForm) {
            const isDest = gpsBooking?._target === "destination";
            const prefix = isDest ? "destination" : "customer";
            setForm((prev) => ({
              ...prev,
              [`${prefix}_lat`]: r.data.location.lat,
              [`${prefix}_lng`]: r.data.location.lng,
              [`${prefix}_location_accuracy`]: r.data.location.accuracy ?? null,
              [`${prefix}_location_address`]: r.data.location.address || null,
            }));
          }
        }
      } catch (_e) { /* silent — keep polling */ }
    };
    const id = setInterval(tick, 4000);
    tick();
    return () => { stopped = true; clearInterval(id); };
  }, [gpsShare?.token, gpsShare?.location, gpsBooking?._inForm, gpsBooking?._target]);

  // R30 B6-cust-merged: search query is derived from the live name/phone
  // inputs the user is currently editing. No standalone popover anymore.
  const custQuery = custFocus === "name"
    ? (form.client_name || "")
    : custFocus === "phone"
      ? (form.client_phone || "")
      : "";
  useEffect(() => {
    const q = (custQuery || "").trim();
    if (!custFocus || q.length < 2) { setCustResults([]); return undefined; }
    let stopped = false;
    const t = setTimeout(async () => {
      setCustBusy(true);
      try {
        const r = await api.get(`/karishma/customers/search`, { params: { q } });
        if (!stopped) setCustResults(r.data?.customers || []);
      } catch { if (!stopped) setCustResults([]); }
      finally { if (!stopped) setCustBusy(false); }
    }, 280);
    return () => { stopped = true; clearTimeout(t); };
  }, [custQuery, custFocus]);

  // R30 B3-final: apply a saved customer to the booking form
  const applyCustomer = (c) => {
    const addrs = c?.addresses || [];
    const pickup = [...addrs].filter(a => a.kind === "pickup" || a.kind === "any")
      .sort((a, b) => (b.last_used_at || "").localeCompare(a.last_used_at || ""))[0];
    const dest = [...addrs].filter(a => a.kind === "destination")
      .sort((a, b) => (b.last_used_at || "").localeCompare(a.last_used_at || ""))[0];
    setForm((prev) => ({
      ...prev,
      client_name: c.name || prev.client_name || "",
      client_phone: c.phone || prev.phone || "",
      client_email: c.email || prev.client_email || "",
      client_address: pickup?.address || prev.client_address || "",
      customer_lat: pickup?.lat ?? null,
      customer_lng: pickup?.lng ?? null,
      destination: dest?.address || prev.destination || "",
      destination_lat: dest?.lat ?? null,
      destination_lng: dest?.lng ?? null,
      // R30 Phase B5w: pre-fill last package weight/size from saved customer
      package_weight_kg: c.last_package_weight_kg ?? prev.package_weight_kg ?? "",
      package_size: c.last_package_size || prev.package_size || "",
    }));
    setCustFocus(null);
    setCustResults([]);
  };

  // R30 B6.2 -- payment link helpers
  const fmtTime = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-JM", { dateStyle: "short", timeStyle: "short" });
    } catch (_e) { return iso.slice(0, 16); }
  };

  const computeQuotePreview = (cost, currency) => {
    const c = parseFloat(cost) || 0;
    if (c <= 0) return null;
    if (currency === "USD") {
      const total = c / 145;
      return {
        currency: "USD",
        cost: c.toFixed(2),
        surcharge: "0.00",
        total: total.toFixed(2),
        surcharge_applied: false,
      };
    }
    // JMD
    const apply = c < 850;
    const surcharge = apply ? c * 0.0345 + 38.50 : 0;
    const total = apply ? c + surcharge : c;
    return {
      currency: "JMD",
      cost: c.toFixed(2),
      surcharge: surcharge.toFixed(2),
      total: total.toFixed(2),
      surcharge_applied: apply,
    };
  };

  const openPaymentPanel = (booking) => {
    setPayBooking(booking);
    const initialCcy = booking.payment_currency || "JMD";
    setPayCurrency(initialCcy);
    setPayQuote(computeQuotePreview(booking.package_cost, initialCcy));
    setPayResult(booking.payment_pay_url ? {
      pay_url: booking.payment_pay_url,
      channels: { copy_url: booking.payment_pay_url },
    } : null);
    setPayCopyOk(false);
  };

  const closePaymentPanel = () => {
    setPayBooking(null);
    setPayResult(null);
    setPayQuote(null);
    setPayCopyOk(false);
  };

  const onChangePayCurrency = (ccy) => {
    setPayCurrency(ccy);
    if (payBooking) setPayQuote(computeQuotePreview(payBooking.package_cost, ccy));
    setPayResult(null);
  };

  const submitPaymentLink = async () => {
    if (!payBooking || paySubmitting) return;
    setPaySubmitting(true);
    try {
      const r = await api.post("/karishma/payments/create-link", {
        package_id: payBooking.id,
        currency: payCurrency,
      });
      setPayResult(r.data);
      // Reflect new payment fields on the row immediately
      setBookings((prev) => prev.map((b) => b.id === payBooking.id ? {
        ...b,
        payment_method: "online",
        payment_status: "pending",
        payment_currency: payCurrency,
        payment_pay_url: r.data?.pay_url,
        payment_provider: r.data?.provider,
      } : b));
      showToast("ok", `Payment link created (${payCurrency}) -- email ${r.data?.channels?.email_sent ? "sent" : "skipped"}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not create payment link.";
      showToast("err", typeof msg === "string" ? msg : "Could not create payment link.");
    } finally {
      setPaySubmitting(false);
    }
  };

  const copyPayUrl = async () => {
    if (!payResult?.pay_url) return;
    try {
      await navigator.clipboard.writeText(payResult.pay_url);
      setPayCopyOk(true);
      setTimeout(() => setPayCopyOk(false), 1800);
    } catch (_e) {
      showToast("err", "Could not copy. Long-press the link instead.");
    }
  };

  const markCOD = async (booking) => {
    try {
      const r = await api.post("/karishma/payments/mark-cod", { package_id: booking.id });
      setBookings((prev) => prev.map((b) => b.id === booking.id ? {
        ...b,
        payment_method: "cod",
        payment_status: "pending_cash",
      } : b));
      showToast("ok", "Marked as Cash on Delivery -- Rohan can now confirm cash on handoff.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not mark COD.";
      showToast("err", typeof msg === "string" ? msg : "Could not mark COD.");
    }
  };

  // R30 B4: Karishma starting-point save
  const openStartingPoint = () => {
    setSpForm({
      address: startingPoint?.address || "",
      lat: startingPoint?.lat ?? null,
      lng: startingPoint?.lng ?? null,
      label: startingPoint?.label || "",
    });
    setSpOpen(true);
  };
  const closeStartingPoint = () => { setSpOpen(false); setSpForm({}); };
  const saveStartingPoint = async () => {
    if (!spForm.address || spForm.lat == null || spForm.lng == null) {
      showToast("err", "Pick an address with verified GPS first.");
      return;
    }
    setSpBusy(true);
    try {
      const r = await api.post("/karishma/starting-point", {
        address: spForm.address,
        lat: Number(spForm.lat),
        lng: Number(spForm.lng),
        label: spForm.label || null,
      });
      setStartingPoint(r.data?.starting_point || null);
      showToast("ok", "Starting point saved.");
      closeStartingPoint();
    } catch (e) {
      showToast("err", e?.response?.data?.detail || "Could not save.");
    } finally { setSpBusy(false); }
  };

  // R30 B2: open the share-location dialog for a booking and create the token.
  // bookingOrForm can be either an existing booking row (with .id) OR an in-flight
  // form snapshot (no .id yet). target = "pickup" | "destination" — drives where
  // submitted coords are written (back-end attaches to the matching slot, and
  // the polling effect writes to the matching form fields).
  const openGpsShare = async (bookingOrForm, target = "pickup") => {
    const inForm = !bookingOrForm?.id;
    setGpsBooking({ ...bookingOrForm, _inForm: inForm, _target: target });
    setGpsShare(null);
    setGpsErr("");
    setGpsEmailOpen(false);
    setGpsEmailTo("");
    setGpsCopied(false);
    setGpsEmailSent(false);
    setGpsBusy(true);
    try {
      const r = await api.post("/karishma/gps-share/create", {
        booking_id: inForm ? null : bookingOrForm.id,
        recipient_name: bookingOrForm.client_name || null,
        recipient_phone: bookingOrForm.client_phone || null,
        role: target,
      });
      setGpsShare(r.data);
      if (inForm) {
        // Stash the token on the form so submitBooking() carries it.
        const tokenKey = target === "destination" ? "destination_gps_share_token" : "gps_share_token";
        setForm((prev) => ({ ...prev, [tokenKey]: r.data.token }));
      }
    } catch (e) {
      setGpsErr(e?.response?.data?.detail || "Could not create share link.");
    } finally {
      setGpsBusy(false);
    }
  };
  const closeGpsShare = () => {
    setGpsBooking(null);
    setGpsShare(null);
    setGpsErr("");
    setGpsEmailOpen(false);
    setGpsEmailTo("");
    setGpsCopied(false);
    setGpsEmailSent(false);
  };
  const copyGpsLink = async () => {
    if (!gpsShare?.share_url) return;
    try {
      await navigator.clipboard.writeText(gpsShare.share_url);
      setGpsCopied(true);
      setTimeout(() => setGpsCopied(false), 2500);
    } catch {
      // Fallback: select-and-prompt
      window.prompt("Copy the link:", gpsShare.share_url);
    }
  };
  const sendGpsEmail = async () => {
    if (!gpsShare?.token) return;
    const to = (gpsEmailTo || "").trim();
    if (!to || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) {
      setGpsErr("Please enter a valid email address.");
      return;
    }
    setGpsErr("");
    setGpsBusy(true);
    try {
      await api.post(`/karishma/gps-share/email/${gpsShare.token}`, {
        recipient_email: to,
        recipient_name: gpsBooking?.client_name || null,
      });
      setGpsEmailSent(true);
      setGpsEmailOpen(false);
    } catch (e) {
      setGpsErr(e?.response?.data?.detail || "Could not send email.");
    } finally {
      setGpsBusy(false);
    }
  };
  const waLink = (() => {
    if (!gpsShare?.share_url) return null;
    const phone = (gpsBooking?.client_phone || "").replace(/[^\d]/g, "");
    const text = encodeURIComponent(
      `Hi${gpsBooking?.client_name ? " " + gpsBooking.client_name : ""}, please share your delivery location for Liantiao GO: ${gpsShare.share_url} (link expires in 30 minutes)`
    );
    return phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`;
  })();
  const smsLink = (() => {
    if (!gpsShare?.share_url) return null;
    const phone = (gpsBooking?.client_phone || "").replace(/[^\d]/g, "");
    const text = encodeURIComponent(
      `Hi${gpsBooking?.client_name ? " " + gpsBooking.client_name : ""}, share your delivery location: ${gpsShare.share_url} (expires in 30 min)`
    );
    return phone ? `sms:${phone}?body=${text}` : `sms:?body=${text}`;
  })();

  const submitBooking = async (e) => {
    e?.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const cost = parseFloat(form.package_cost);
      if (!cost || cost <= 0) { showToast("err", "Cost must be a positive number."); return; }
      if (!form.client_name || !form.client_address || !form.parish || !form.destination) {
        showToast("err", "Please fill in all required fields.");
        return;
      }
      const r = await api.post("/karishma/bookings", {
        package_cost: cost,
        client_name: form.client_name,
        client_phone: form.client_phone || null,
        client_email: (form.client_email || "").trim(),
        client_address: form.client_address,
        parish: form.parish,
        destination: form.destination,
        destination_parish: form.destination_parish || null,
        notes: form.notes || null,
        assigned_driver_id: form.assigned_driver_id || null,
        assigned_vehicle_id: form.assigned_vehicle_id || null,
        // R30 B2: pickup GPS captured during the booking flow (optional)
        customer_lat: form.customer_lat ?? null,
        customer_lng: form.customer_lng ?? null,
        customer_location_accuracy: form.customer_location_accuracy ?? null,
        customer_location_address: form.customer_location_address || null,
        gps_share_token: form.gps_share_token || null,
        // R30 B2b: destination GPS captured during the booking flow (optional)
        destination_lat: form.destination_lat ?? null,
        destination_lng: form.destination_lng ?? null,
        destination_location_accuracy: form.destination_location_accuracy ?? null,
        destination_location_address: form.destination_location_address || null,
        destination_gps_share_token: form.destination_gps_share_token || null,
        // R30 Phase B5w: optional package weight + size (do not block submit)
        package_weight_kg: form.package_weight_kg !== "" && form.package_weight_kg != null
          ? parseFloat(form.package_weight_kg)
          : null,
        package_size: form.package_size || null,
      });
      showToast("ok", `Booked. Commission: ${fmtJMD(r.data?.commission_jmd)}`);
      closeGpsShare();
      closeForm();
      await reload();
    } catch (e2) {
      showToast("err", e2?.response?.data?.detail || "Booking failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitDriver = async (e) => {
    e?.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      if (!form.name || !form.phone) { showToast("err", "Name and phone required."); return; }
      await api.post("/karishma/drivers", {
        name: form.name, phone: form.phone,
        email: form.email || null, license_plate: form.license_plate || null,
        notes: form.notes || null,
      });
      showToast("ok", "Driver proposal submitted — pending oversight approval.");
      closeForm();
    } catch (e2) {
      showToast("err", e2?.response?.data?.detail || "Could not submit.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitVehicle = async (e) => {
    e?.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      if (!form.license_plate || !form.make || !form.model) {
        showToast("err", "Plate, make, and model required."); return;
      }
      await api.post("/karishma/vehicles", {
        license_plate: form.license_plate, make: form.make, model: form.model,
        year: form.year ? parseInt(form.year) : null,
        colour: form.colour || null, notes: form.notes || null,
      });
      showToast("ok", "Vehicle proposal submitted — pending oversight approval.");
      closeForm();
    } catch (e2) {
      showToast("err", e2?.response?.data?.detail || "Could not submit.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="karishma-portal-page" className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50 to-emerald-100 p-4 sm:p-6 pb-24">
      <div className="max-w-4xl mx-auto">
        <header className="flex items-center justify-between mb-6">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 border border-emerald-300 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-emerald-800">
              <KeyRound className="w-3.5 h-3.5" /> PIN authenticated
            </div>
            <h1 className="text-3xl sm:text-4xl font-black text-slate-900 mt-2">
              Welcome, Karishma <span className="text-emerald-700">.</span>
            </h1>
            <p className="text-slate-600 text-sm mt-1">Off-system booking portal · 5% commission per package</p>
          </div>
          <button
            data-testid="karishma-portal-open-payments-reconciliation"
            onClick={() => navigate("/portal/karishma/payments")}
            className="inline-flex items-center gap-1 rounded-full bg-slate-900 text-white px-3 py-1.5 text-[12px] font-semibold hover:bg-slate-800 mr-2"
          >Payments & Reconciliation</button>
          <button
            data-testid="karishma-portal-signout"
            onClick={handleSignOut}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white hover:bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </header>

        {/* Summary cards */}
        <section data-testid="karishma-summary" className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <Stat label="Bookings" value={summary?.total_bookings ?? "—"} testid="kp-stat-bookings" />
          <Stat label="Revenue" value={fmtJMD(summary?.total_revenue_jmd)} testid="kp-stat-revenue" />
          <Stat label="Commission earned" value={fmtJMD(summary?.total_commission_jmd)} testid="kp-stat-commission" emerald />
          <Stat label="Pending payout" value={fmtJMD(summary?.commission_pending_jmd)} testid="kp-stat-pending" amber />
          <Stat label="Customers" value={summary?.unique_customers ?? "—"} testid="kp-stat-customers" />
        </section>

        {/* R30 B5p: Top performers */}
        {summary && (
          <section data-testid="kp-top-performers" className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-4">
              <h3 className="text-sm font-bold text-slate-900 mb-2">Top drivers</h3>
              {(summary.top_drivers || []).length === 0 ? (
                <p className="text-[12px] text-slate-500">No assigned-driver data yet.</p>
              ) : (
                <ul className="space-y-1">
                  {(summary.top_drivers || []).map((d, i) => (
                    <li key={d.driver_id} data-testid={`kp-top-driver-${d.driver_id}`} className="flex items-center justify-between text-[13px]">
                      <span><span className="text-slate-400 font-mono mr-2">#{i + 1}</span><span className="text-slate-900 font-semibold">{d.name}</span></span>
                      <span className="text-slate-500">{d.bookings} booking{d.bookings === 1 ? "" : "s"}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {/* R30-B7.2-ux-topcust: collapse the growing list behind a dropdown */}
            <details data-testid="kp-top-customers-details" className="rounded-3xl bg-white border border-slate-200 shadow-sm p-4">
              <summary className="cursor-pointer select-none list-none flex items-center justify-between">
                <h3 className="text-sm font-bold text-slate-900">
                  Top customers <span className="ml-1 text-slate-400 font-normal">({(summary.top_customers || []).length})</span>
                </h3>
                <span aria-hidden="true" className="text-slate-400 text-xs">▾</span>
              </summary>
              <div className="mt-3">
                {(summary.top_customers || []).length === 0 ? (
                  <p className="text-[12px] text-slate-500">No saved customers yet.</p>
                ) : (
                  <ul className="space-y-1 max-h-[40vh] overflow-y-auto pr-1">
                    {(summary.top_customers || []).map((c, i) => (
                      <li key={c.id} data-testid={`kp-top-customer-${c.id}`} className="flex items-center justify-between text-[13px]">
                        <span><span className="text-slate-400 font-mono mr-2">#{i + 1}</span><span className="text-slate-900 font-semibold">{c.name || c.phone || c.email}</span></span>
                        <span className="text-slate-500">{c.booking_count || 0} booking{(c.booking_count || 0) === 1 ? "" : "s"}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </details>
          </section>
        )}

        {/* R30 B4: Karishma starting-point card */}
        <section data-testid="kp-starting-point-card" className="rounded-3xl bg-white border border-emerald-200 shadow-sm p-5 sm:p-6 mb-6">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <h2 className="text-lg font-bold text-slate-900 inline-flex items-center gap-2">
                <MapPin size={16} className="text-emerald-700" /> Starting point
              </h2>
              <p className="text-[12px] text-slate-500 mt-0.5">Your home base. Used to estimate distance to every pickup.</p>
              {startingPoint?.address ? (
                <div className="mt-3 space-y-1" data-testid="kp-starting-point-current">
                  <div className="text-sm font-semibold text-slate-900">{startingPoint.label || "Home"}</div>
                  <div className="text-[13px] text-slate-700">{startingPoint.address}</div>
                  <div className="text-[11px] font-mono tabular-nums text-emerald-700">{Number(startingPoint.lat).toFixed(6)}, {Number(startingPoint.lng).toFixed(6)}</div>
                </div>
              ) : (
                <div className="mt-3 text-[13px] text-slate-500" data-testid="kp-starting-point-empty">No starting point set yet.</div>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              {startingPoint?.lat != null && startingPoint?.lng != null && (
                <a
                  href={`https://www.google.com/maps?q=${startingPoint.lat},${startingPoint.lng}`}
                  target="_blank" rel="noreferrer"
                  data-testid="kp-starting-point-open-maps"
                  className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-[3px] text-[11px] font-semibold hover:bg-emerald-100"
                >
                  <Navigation size={11} /> Open in Maps
                </a>
              )}
              <button
                type="button"
                onClick={openStartingPoint}
                data-testid="kp-starting-point-edit"
                className="inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-3 py-[5px] text-[12px] font-semibold hover:bg-emerald-700"
              >
                <MapPin size={12} /> {startingPoint?.address ? "Edit" : "Set starting point"}
              </button>
            </div>
          </div>
        </section>

        {/* C2: withdrawal balance + request */}
        <section data-testid="kp-withdrawal-card" className="rounded-3xl bg-white border border-emerald-200 shadow-sm p-5 sm:p-6 mb-6">
          {/* R30-B7.2-ux-commission: mobile-first stacking + smaller numbers */}
          <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 mb-3">
            <h2 className="text-lg font-bold text-slate-900">Commission balance</h2>
            <span className="text-[11px] text-slate-500">Min withdrawal J${(c2Balance?.minimum_withdrawal_jmd ?? 500).toLocaleString("en-JM")} · {(c2Balance?.processing_fee_rate ?? 0) * 100}% fee</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div data-testid="kp-c2-available" className="rounded-2xl bg-emerald-50 border border-emerald-200 p-3">
              <div className="text-[11px] uppercase tracking-wide text-emerald-700 font-semibold">Available</div>
              <div className="text-xl sm:text-2xl font-bold tabular-nums text-emerald-900 mt-1 break-all">{fmtJMD(c2Balance?.available_jmd)}</div>
              <div className="text-[10px] text-emerald-700 mt-1">{c2Balance?.ledger_rows_pending ?? 0} rows</div>
            </div>
            <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3">
              <div className="text-[11px] uppercase tracking-wide text-amber-700 font-semibold">In review</div>
              <div className="text-xl sm:text-2xl font-bold tabular-nums text-amber-900 mt-1 break-all">{fmtJMD(c2Balance?.reserved_jmd)}</div>
              <div className="text-[10px] text-amber-700 mt-1">{c2Balance?.ledger_rows_reserved ?? 0} rows</div>
            </div>
            <div className="rounded-2xl bg-slate-50 border border-slate-200 p-3">
              <div className="text-[11px] uppercase tracking-wide text-slate-600 font-semibold">Paid</div>
              <div className="text-xl sm:text-2xl font-bold tabular-nums text-slate-900 mt-1 break-all">{fmtJMD(c2Balance?.paid_jmd)}</div>
              <div className="text-[10px] text-slate-600 mt-1">{c2Balance?.ledger_rows_paid ?? 0} rows</div>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              data-testid="kp-c2-request-btn"
              type="button"
              onClick={() => { setC2Show(true); setC2Amount(""); setC2Notes(""); }}
              disabled={(c2Balance?.available_jmd ?? 0) < (c2Balance?.minimum_withdrawal_jmd ?? 500)}
              className="rounded-lg bg-emerald-700 text-white px-4 py-2 text-sm font-semibold disabled:opacity-50"
            >
              Request withdrawal
            </button>
            {c2Balance?.available_jmd > 0 && (
              <button
                data-testid="kp-c2-withdraw-all-btn"
                type="button"
                onClick={() => { setC2Show(true); setC2Amount(String(c2Balance.available_jmd)); setC2Notes(""); }}
                className="rounded-lg bg-emerald-50 text-emerald-800 border border-emerald-300 px-3 py-2 text-sm font-semibold"
              >
                Withdraw all ({fmtJMD(c2Balance.available_jmd)})
              </button>
            )}
            <button
              data-testid="kp-c2-refresh-btn"
              type="button"
              onClick={c2Reload}
              className="text-xs text-slate-600 underline ml-auto"
            >
              Refresh
            </button>
          </div>
          <details data-testid="kp-c2-history-details" className="mt-4 border-t border-slate-100 pt-3">
            <summary className="cursor-pointer select-none text-sm font-semibold text-slate-800 list-none flex items-center justify-between">
              <span>Withdrawal history ({c2History.length})</span>
              <span aria-hidden="true" className="text-slate-400 text-xs">▾</span>
            </summary>
            {c2History.length === 0 ? (
              <p className="text-xs text-slate-500 mt-2">No withdrawals yet.</p>
            ) : (
              <ul data-testid="kp-c2-history-list" className="mt-2 max-h-[40vh] overflow-y-auto divide-y divide-slate-100 pr-1">
                {c2History.map((w) => (
                  <li key={w.id} data-testid={`kp-c2-w-${w.id}`} className="py-2">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="font-semibold text-slate-900 tabular-nums">{fmtJMD(w.requested_amount)}</div>
                      <span className={
                        "rounded-full border px-2 py-[1px] text-[11px] font-semibold " +
                        (w.status === "paid" ? "bg-emerald-50 border-emerald-200 text-emerald-800" :
                         w.status === "rejected" ? "bg-rose-50 border-rose-200 text-rose-800" :
                         "bg-amber-50 border-amber-200 text-amber-800")
                      }>{w.status}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {(w.created_at || "").slice(0, 10)} · {(w.linked_ledger_ids || []).length} rows
                      {w.notes ? ` · "${w.notes}"` : ""}
                    </div>
                    {w.admin_notes && (
                      <div className="text-[11px] text-slate-600 italic mt-0.5">admin: {w.admin_notes}</div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </details>
        </section>

        {/* C2: submit modal (lightweight — uses same Modal component) */}
        {c2Show && (
          <Modal title="Request commission withdrawal" onClose={() => setC2Show(false)} testid="kp-c2-modal">
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const amt = Number(c2Amount);
                if (!amt || amt <= 0) { toast.error("Enter an amount"); return; }
                setC2Submitting(true);
                try {
                  await api.post("/karishma/withdrawals", { amount: amt, notes: c2Notes.trim() || undefined });
                  toast.success("Withdrawal request submitted");
                  setC2Show(false);
                  await c2Reload();
                  await reload(); // refresh stat cards
                } catch (err) {
                  toast.error(err?.response?.data?.detail || err?.response?.data?.error?.message || "Submit failed");
                } finally { setC2Submitting(false); }
              }}
              className="space-y-3"
            >
              <Field label="Amount JMD" type="number" min="500" step="0.01" value={c2Amount} onChange={setC2Amount} />
              <Textarea label="Notes (optional)" value={c2Notes} onChange={setC2Notes} />
              <p className="text-[11px] text-slate-500">
                Amount must equal a cumulative FIFO prefix-sum of pending rows. The system will lock the
                exact rows that match. A J${(c2Balance?.minimum_withdrawal_jmd ?? 500)} minimum applies.
              </p>
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setC2Show(false)}
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
                >Cancel</button>
                <button
                  data-testid="kp-c2-modal-submit"
                  type="submit"
                  disabled={c2Submitting}
                  className="rounded-lg bg-emerald-700 text-white px-4 py-2 text-sm font-semibold disabled:opacity-50"
                >{c2Submitting ? "Submitting..." : "Submit"}</button>
              </div>
            </form>
          </Modal>
        )}


        {/* Action buttons */}
        <section className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <ActionBtn onClick={() => openForm("book")} icon={Plus} label="Book a package" testid="kp-btn-book" emerald />
          <ActionBtn onClick={() => openForm("driver")} icon={UserPlus} label="Add Liantiao driver" testid="kp-btn-add-driver" />
          <ActionBtn onClick={() => openForm("vehicle")} icon={Truck} label="Add Liantiao vehicle" testid="kp-btn-add-vehicle" />
        </section>

        {/* R29: Vehicle ops — expense (C1: collapsible) */}
        <details data-testid="karishma-vehicle-expense-details" className="mb-6 rounded-3xl bg-white border border-slate-200 shadow-sm overflow-hidden">
          <summary className="cursor-pointer select-none font-bold text-slate-900 px-5 py-4 sm:px-6 hover:bg-slate-50 list-none flex items-center justify-between">
            <span className="flex items-center gap-2"><FileText className="w-4 h-4 text-emerald-700" /> Log vehicle expense</span>
            <span aria-hidden="true" className="text-slate-400 text-xs">▾</span>
          </summary>
          <section data-testid="karishma-vehicle-expense" className="px-5 pb-5 sm:px-6 sm:pb-6 border-t border-slate-100 pt-4">
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              const amt = Number(vexpAmount);
              if (!amt || amt <= 0) { toast.error("Enter an amount"); return; }
              setVexpSubmitting(true);
              try {
                const today = new Date().toISOString().slice(0, 10);
                await api.post("/admin/vehicle-ops/expenses", {
                  type: vexpType,
                  amount: amt,
                  date: today,
                  note: vexpNote.trim() || undefined,
                });
                toast.success("Expense logged");
                setVexpAmount(""); setVexpNote("");
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Could not log expense");
              } finally { setVexpSubmitting(false); }
            }}
            className="grid grid-cols-1 sm:grid-cols-4 gap-2"
          >
            <select
              data-testid="kp-expense-type"
              value={vexpType}
              onChange={(e) => setVexpType(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white text-slate-900"
            >
              <option value="fuel">Fuel / Gas</option>
              <option value="maintenance">Maintenance</option>
              <option value="insurance">Insurance</option>
              <option value="fitness">Fitness</option>
              <option value="road_license">Road License</option>
              <option value="other">Other</option>
            </select>
            <input
              data-testid="kp-expense-amount"
              type="number"
              inputMode="numeric"
              min="1"
              placeholder="Amount JMD"
              value={vexpAmount}
              onChange={(e) => setVexpAmount(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white text-slate-900 placeholder:text-slate-400"
            />
            <input
              data-testid="kp-expense-note"
              type="text"
              placeholder="Note (optional)"
              maxLength={300}
              value={vexpNote}
              onChange={(e) => setVexpNote(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white text-slate-900 placeholder:text-slate-400"
            />
            <button
              data-testid="kp-expense-submit"
              type="submit"
              disabled={vexpSubmitting}
              className="rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-semibold disabled:opacity-50"
            >
              {vexpSubmitting ? "..." : "Log"}
            </button>
          </form>
          </section>
        </details>

        {/* R29: Vehicle ops — income (C1: collapsible) */}
        <details data-testid="karishma-vehicle-income-details" className="mb-6 rounded-3xl bg-white border border-slate-200 shadow-sm overflow-hidden">
          <summary className="cursor-pointer select-none font-bold text-slate-900 px-5 py-4 sm:px-6 hover:bg-slate-50 list-none flex items-center justify-between">
            <span className="flex items-center gap-2"><FileText className="w-4 h-4 text-emerald-700" /> Log vehicle income (off-platform)</span>
            <span aria-hidden="true" className="text-slate-400 text-xs">▾</span>
          </summary>
          <section data-testid="karishma-vehicle-income" className="px-5 pb-5 sm:px-6 sm:pb-6 border-t border-slate-100 pt-4">
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              const amt = Number(vincAmount);
              if (!amt || amt <= 0) { toast.error("Enter an amount"); return; }
              if (!vincDescription.trim()) { toast.error("Description required"); return; }
              setVincSubmitting(true);
              try {
                const today = new Date().toISOString().slice(0, 10);
                await api.post("/admin/vehicle-ops/income", {
                  amount: amt,
                  date: today,
                  description: vincDescription.trim(),
                  customer_name: vincCustomer.trim() || undefined,
                });
                toast.success("Income logged");
                setVincAmount(""); setVincDescription(""); setVincCustomer("");
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Could not log income");
              } finally { setVincSubmitting(false); }
            }}
            className="grid grid-cols-1 sm:grid-cols-4 gap-2"
          >
            <input
              data-testid="kp-income-amount"
              type="number"
              inputMode="numeric"
              min="1"
              placeholder="Amount JMD"
              value={vincAmount}
              onChange={(e) => setVincAmount(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white text-slate-900 placeholder:text-slate-400"
            />
            <input
              data-testid="kp-income-customer"
              type="text"
              placeholder="Customer name (optional)"
              maxLength={120}
              value={vincCustomer}
              onChange={(e) => setVincCustomer(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white text-slate-900 placeholder:text-slate-400"
            />
            <input
              data-testid="kp-income-description"
              type="text"
              placeholder="Description"
              maxLength={300}
              value={vincDescription}
              onChange={(e) => setVincDescription(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white text-slate-900 placeholder:text-slate-400"
            />
            <button
              data-testid="kp-income-submit"
              type="submit"
              disabled={vincSubmitting}
              className="rounded-lg bg-emerald-700 text-white px-4 py-2 text-sm font-semibold disabled:opacity-50"
            >
              {vincSubmitting ? "..." : "Log"}
            </button>
          </form>
          </section>
        </details>

        {/* Bookings list — C1: filters + scrollable */}
        <section data-testid="karishma-bookings-list" className="rounded-3xl bg-white border border-slate-200 shadow-sm p-5 sm:p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-emerald-700" />
              Recent bookings
            </h2>
            <span data-testid="kp-bookings-count" className="text-xs text-slate-500">
              {filteredBookings.length === bookings.length
                ? `${bookings.length} total`
                : `${filteredBookings.length} of ${bookings.length}`}
            </span>
          </div>

          {/* C1: filter bar (frontend-only) */}
          <div data-testid="kp-filter-bar" className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
            <select
              data-testid="kp-filter-month"
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white text-slate-900"
            >
              <option value="">All months</option>
              {monthOptions.map((m) => (<option key={m} value={m}>{m}</option>))}
            </select>
            <select
              data-testid="kp-filter-parish"
              value={filterParish}
              onChange={(e) => setFilterParish(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white text-slate-900"
            >
              <option value="">All parishes</option>
              {parishOptions.map((p) => (<option key={p} value={p}>{p}</option>))}
            </select>
            <select
              data-testid="kp-filter-status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white text-slate-900"
            >
              <option value="">All statuses</option>
              {statusOptions.map((s) => (<option key={s} value={s}>{s}</option>))}
            </select>
            <input
              data-testid="kp-filter-search"
              type="text"
              placeholder="Search name / phone / id"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white text-slate-900 placeholder:text-slate-400 col-span-2 sm:col-span-1"
            />
          </div>

          {filteredBookings.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">
              {bookings.length === 0
                ? 'No bookings yet. Click "Book a package" to capture your first one.'
                : "No bookings match the current filters."}
            </p>
          ) : (
            <ul data-testid="kp-bookings-scrollarea" className="max-h-[60vh] overflow-y-auto divide-y divide-slate-100 pr-1">
              {filteredBookings.map((b) => (
                <li key={b.id} data-testid={`kp-booking-${b.id}`} className="py-3">
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="font-semibold text-slate-900">{b.client_name}</div>
                    <div className="font-mono tabular-nums text-emerald-700 font-bold">{fmtJMD(b.package_cost)}</div>
                  </div>
                  <div className="text-[12px] text-slate-600 mt-0.5">
                    {b.parish} · {b.destination}
                  </div>
                  {b.client_phone && (
                    <div className="text-[11px] text-slate-500 mt-0.5">{b.client_phone}</div>
                  )}
                  <div className="flex flex-wrap gap-2 mt-1.5 text-[11px]">
                    <span className="rounded-full bg-emerald-50 border border-emerald-200 px-2 py-[1px] text-emerald-700 font-semibold">
                      Commission {fmtJMD(b.commission_amount_jmd)}
                    </span>
                    <span className={`rounded-full border px-2 py-[1px] font-semibold ${
                      b.commission_status === "paid"
                        ? "bg-slate-100 border-slate-300 text-slate-700"
                        : "bg-amber-50 border-amber-200 text-amber-800"
                    }`}>{b.commission_status}</span>
                    <span className="rounded-full border px-2 py-[1px] font-semibold bg-slate-50 border-slate-200 text-slate-700">
                      {b.status}
                    </span>
                    <span className="text-slate-500">{(b.created_at || "").slice(0, 10)}</span>
                  </div>
                  {/* R30 B2: customer location status + request button */}
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {b.customer_lat != null && b.customer_lng != null ? (
                      <a
                        href={`https://www.google.com/maps?q=${b.customer_lat},${b.customer_lng}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`kp-booking-customer-coords-${b.id}`}
                        className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-[2px] text-[11px] text-emerald-700 font-semibold hover:bg-emerald-100"
                      >
                        <Navigation size={11} /> Customer GPS shared
                      </a>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 border border-slate-200 px-2 py-[2px] text-[11px] text-slate-500">
                        <MapPin size={11} /> No customer GPS yet
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => openGpsShare(b)}
                      data-testid={`kp-booking-request-gps-${b.id}`}
                      className="inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-3 py-[3px] text-[11px] font-semibold hover:bg-emerald-700"
                    >
                      <MapPin size={11} /> Request location
                    </button>
                  </div>
                  {/* R30 B6.2: payment status + actions */}
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {(() => {
                      const s = b.payment_status;
                      const cfg =
                        s === "paid"         ? { label: "Paid online",  cls: "bg-emerald-100 border-emerald-300 text-emerald-800" } :
                        s === "cash_paid"    ? { label: "Cash collected", cls: "bg-emerald-100 border-emerald-300 text-emerald-800" } :
                        s === "pending_cash" ? { label: "Cash on delivery (pending)", cls: "bg-amber-50 border-amber-200 text-amber-800" } :
                        s === "pending"      ? { label: "Awaiting payment", cls: "bg-sky-50 border-sky-200 text-sky-800" } :
                        s === "failed"       ? { label: "Payment failed", cls: "bg-rose-50 border-rose-200 text-rose-800" } :
                                               { label: "No payment yet", cls: "bg-slate-50 border-slate-200 text-slate-700" };
                      return (
                        <span data-testid={`kp-booking-pay-status-${b.id}`} className={`rounded-full border px-2 py-[1px] text-[11px] font-semibold ${cfg.cls}`}>
                          {cfg.label}
                          {b.paid_at && (s === "paid" || s === "cash_paid") && (
                            <span className="ml-1 font-normal opacity-80">· {fmtTime(b.paid_at)}</span>
                          )}
                        </span>
                      );
                    })()}
                    {!["paid", "cash_paid"].includes(b.payment_status || "") && (
                      <>
                        <button
                          type="button"
                          onClick={() => openPaymentPanel(b)}
                          data-testid={`kp-booking-send-pay-${b.id}`}
                          className="inline-flex items-center gap-1 rounded-full bg-slate-900 text-white px-3 py-[3px] text-[11px] font-semibold hover:bg-slate-800"
                        >
                          <Mail size={11} /> {b.payment_pay_url ? "Resend payment link" : "Send payment link"}
                        </button>
                        {b.payment_method !== "cod" && (
                          <button
                            type="button"
                            onClick={() => markCOD(b)}
                            data-testid={`kp-booking-mark-cod-${b.id}`}
                            className="inline-flex items-center gap-1 rounded-full bg-amber-600 text-white px-3 py-[3px] text-[11px] font-semibold hover:bg-amber-700"
                          >
                            <FileText size={11} /> Cash on delivery
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <p className="text-center text-xs text-slate-500 mt-6">
          Liantiao GO · {fmtJMD(0).split("$")[0]} = JMD · Powered by Quick PIN access
        </p>
      </div>

      {/* Modal forms */}
      {activeForm === "book" && (
        <Modal title="Book an off-system package" onClose={closeForm} testid="kp-modal-book">
          <form onSubmit={submitBooking} className="space-y-3" data-testid="kp-form-book">
            <Field testid="kp-input-cost" label="Package cost (JMD)" type="number" min="0" step="0.01" value={form.package_cost || ""} onChange={(v) => setField("package_cost", v)} />
            {/* R30-B6-cust-merged: client name/phone with inline saved-customer auto-suggest */}
            <div className="relative">
              <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">Client name</label>
              <input
                data-testid="kp-input-client-name"
                type="text"
                autoComplete="off"
                value={form.client_name || ""}
                onChange={(e) => setField("client_name", e.target.value)}
                onFocus={() => setCustFocus("name")}
                onBlur={() => setTimeout(() => setCustFocus((f) => (f === "name" ? null : f)), 150)}
                className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-base text-slate-900 placeholder:text-slate-400 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                placeholder="Type name, phone or email to search saved customers…"
              />
              {custFocus === "name" && custQuery.trim().length >= 2 && (
                <CustomerSuggestPanel
                  busy={custBusy}
                  query={custQuery}
                  results={custResults}
                  onPick={applyCustomer}
                />
              )}
            </div>
            <div className="relative">
              <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">Client phone (required)</label>
              <input
                data-testid="kp-input-client-phone"
                type="tel"
                inputMode="numeric"
                autoComplete="off"
                value={form.client_phone || ""}
                onChange={(e) => setField("client_phone", (e.target.value || "").replace(/[^0-9+\-\s()]/g, ""))}
                onFocus={() => setCustFocus("phone")}
                onBlur={() => setTimeout(() => setCustFocus((f) => (f === "phone" ? null : f)), 150)}
                className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-base text-slate-900 placeholder:text-slate-400 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                placeholder="Type name, phone or email to search saved customers…"
              />
              {custFocus === "phone" && custQuery.trim().length >= 2 && (
                <CustomerSuggestPanel
                  busy={custBusy}
                  query={custQuery}
                  results={custResults}
                  onPick={applyCustomer}
                />
              )}
            </div>
            <Field testid="kp-input-client-email" label="Client email (required)" type="email" value={form.client_email || ""} onChange={(v) => setField("client_email", v)} placeholder="customer@example.com" autoComplete="off" />
            {/* R30 B3-final: smart address input with map preview */}
            <SmartAddressInput
              testid="kp-input-client-address"
              label="Pickup address"
              value={form.client_address || ""}
              onChange={(v) => setField("client_address", v)}
              lat={form.customer_lat}
              lng={form.customer_lng}
              onPick={(r) => setForm((p) => ({ ...p, client_address: r.display_name, customer_lat: r.lat, customer_lng: r.lng }))}
              onClear={() => setForm((p) => ({ ...p, customer_lat: null, customer_lng: null }))}
              suggestions={pickupAddressSuggestions}
              placeholder="Start typing — Jamaica addresses will suggest"
            />
            <Select testid="kp-input-parish" label="Parish" value={form.parish || ""} onChange={(v) => setField("parish", v)} options={parishes} placeholder="Select parish…" />
            <SmartAddressInput
              testid="kp-input-destination"
              label="Destination"
              value={form.destination || ""}
              onChange={(v) => setField("destination", v)}
              lat={form.destination_lat}
              lng={form.destination_lng}
              onPick={(r) => setForm((p) => ({ ...p, destination: r.display_name, destination_lat: r.lat, destination_lng: r.lng }))}
              onClear={() => setForm((p) => ({ ...p, destination_lat: null, destination_lng: null }))}
              suggestions={destinationSuggestions}
              placeholder="Start typing — Jamaica destinations will suggest"
            />
            <Select testid="kp-input-destination-parish" label="Destination parish (optional)" value={form.destination_parish || ""} onChange={(v) => setField("destination_parish", v)} options={parishes} placeholder="Select destination parish…" />
            {/* R30 Phase B5w: package weight + size (both optional) */}
            <div className="grid grid-cols-2 gap-3">
              <Field
                testid="kp-input-package-weight"
                label="Package weight (kg, optional)"
                type="number"
                min="0"
                step="0.1"
                value={form.package_weight_kg ?? ""}
                onChange={(v) => setField("package_weight_kg", v)}
                placeholder="e.g. 2.5"
              />
              <Select
                testid="kp-input-package-size"
                label="Package size (optional)"
                value={form.package_size || ""}
                onChange={(v) => setField("package_size", v)}
                options={[
                  { value: "S",  label: "Small" },
                  { value: "M",  label: "Medium" },
                  { value: "L",  label: "Large" },
                  { value: "XL", label: "Extra Large" },
                ]}
                placeholder="Select size…"
              />
            </div>
            <Select testid="kp-input-driver" label="Assign driver (optional)" value={form.assigned_driver_id || ""} onChange={(v) => setField("assigned_driver_id", v)}
              options={drivers.map((d) => ({ value: d.id, label: d.name || d.id }))} placeholder="Unassigned" />
            <Select testid="kp-input-vehicle" label="Assign vehicle (optional)" value={form.assigned_vehicle_id || ""} onChange={(v) => setField("assigned_vehicle_id", v)}
              options={vehicles.map((v) => ({ value: v.id, label: `${v.license_plate || v.id} ${v.vehicle_make ? "· " + v.vehicle_make : ""}` }))} placeholder="Unassigned" />
            <Textarea testid="kp-input-notes" label="Notes (optional)" value={form.notes || ""} onChange={(v) => setField("notes", v)} />

            {/* R30 B2: pickup GPS request */}
            <GpsRequestRow
              title="Pickup location"
              hint="Optional. Send the customer a 30-min link to share where the package will be picked up."
              lat={form.customer_lat}
              lng={form.customer_lng}
              testidPrefix="kp-book-gps"
              onRequest={() => openGpsShare({
                client_name: form.client_name || null,
                client_phone: form.client_phone || null,
              }, "pickup")}
              onClear={() => setForm((p) => ({ ...p, customer_lat: null, customer_lng: null, customer_location_accuracy: null, customer_location_address: null, gps_share_token: null }))}
            />
            {/* R30 B2b: destination GPS request */}
            <GpsRequestRow
              title="Destination location"
              hint="Optional. Send the recipient a 30-min link to share where the driver should drop off."
              lat={form.destination_lat}
              lng={form.destination_lng}
              testidPrefix="kp-book-dest-gps"
              onRequest={() => openGpsShare({
                client_name: form.client_name || null,
                client_phone: form.client_phone || null,
              }, "destination")}
              onClear={() => setForm((p) => ({ ...p, destination_lat: null, destination_lng: null, destination_location_accuracy: null, destination_location_address: null, destination_gps_share_token: null }))}
            />

            <SubmitRow submitting={submitting} testid="kp-submit-book" label="Save booking" />
          </form>
        </Modal>
      )}

      {activeForm === "driver" && (
        <Modal title="Add Liantiao driver" onClose={closeForm} testid="kp-modal-driver">
          <form onSubmit={submitDriver} className="space-y-3" data-testid="kp-form-driver">
            <Field testid="kp-drv-name" label="Full name" value={form.name || ""} onChange={(v) => setField("name", v)} />
            <Field testid="kp-drv-phone" label="Phone" value={form.phone || ""} onChange={(v) => setField("phone", v)} />
            <Field testid="kp-drv-email" label="Email (optional)" value={form.email || ""} onChange={(v) => setField("email", v)} />
            <Field testid="kp-drv-plate" label="License plate hint (optional)" value={form.license_plate || ""} onChange={(v) => setField("license_plate", v)} />
            <Textarea testid="kp-drv-notes" label="Notes (optional)" value={form.notes || ""} onChange={(v) => setField("notes", v)} />
            <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <AlertCircle className="w-3 h-3 inline mr-1" />
              Submission goes to Mark for approval before driver appears in dropdowns.
            </p>
            <SubmitRow submitting={submitting} testid="kp-submit-driver" label="Submit for approval" />
          </form>
        </Modal>
      )}

      {activeForm === "vehicle" && (
        <Modal title="Add Liantiao vehicle" onClose={closeForm} testid="kp-modal-vehicle">
          <form onSubmit={submitVehicle} className="space-y-3" data-testid="kp-form-vehicle">
            <Field testid="kp-veh-plate" label="License plate" value={form.license_plate || ""} onChange={(v) => setField("license_plate", v)} />
            <Field testid="kp-veh-make" label="Make" value={form.make || ""} onChange={(v) => setField("make", v)} />
            <Field testid="kp-veh-model" label="Model" value={form.model || ""} onChange={(v) => setField("model", v)} />
            <Field testid="kp-veh-year" label="Year (optional)" type="number" value={form.year || ""} onChange={(v) => setField("year", v)} />
            <Field testid="kp-veh-colour" label="Colour (optional)" value={form.colour || ""} onChange={(v) => setField("colour", v)} />
            <Textarea testid="kp-veh-notes" label="Notes (optional)" value={form.notes || ""} onChange={(v) => setField("notes", v)} />
            <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <AlertCircle className="w-3 h-3 inline mr-1" />
              Submission goes to Mark for approval before vehicle appears in dropdowns.
            </p>
            <SubmitRow submitting={submitting} testid="kp-submit-vehicle" label="Submit for approval" />
          </form>
        </Modal>
      )}

      {/* R30 B4: Starting-point edit modal */}
      {spOpen && (
        <Modal title="Set starting point" onClose={closeStartingPoint} testid="kp-modal-starting-point">
          <div className="space-y-3">
            <SmartAddressInput
              testid="kp-sp-address"
              label="Address"
              value={spForm.address || ""}
              onChange={(v) => setSpForm((p) => ({ ...p, address: v }))}
              lat={spForm.lat}
              lng={spForm.lng}
              onPick={(r) => setSpForm((p) => ({ ...p, address: r.display_name, lat: r.lat, lng: r.lng }))}
              onClear={() => setSpForm((p) => ({ ...p, lat: null, lng: null }))}
              suggestions={[]}
              placeholder="Type your home/base address…"
            />
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">Label (optional)</label>
              <input
                data-testid="kp-sp-label"
                type="text"
                value={spForm.label || ""}
                onChange={(e) => setSpForm((p) => ({ ...p, label: e.target.value }))}
                placeholder="Home, Office…"
                className="w-full mt-1 rounded-xl border border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 px-3 py-2.5 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
              />
            </div>
            <button
              type="button"
              onClick={saveStartingPoint}
              disabled={spBusy || !spForm.address || spForm.lat == null}
              data-testid="kp-sp-save"
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 text-white px-3 py-2.5 text-sm font-bold hover:bg-emerald-700 disabled:opacity-60"
            >
              {spBusy ? <><Loader2 className="animate-spin" size={14} /> Saving…</> : <><CheckCircle2 size={14} /> Save starting point</>}
            </button>
          </div>
        </Modal>
      )}

      {/* R30 B6.2 -- Payment link modal */}
      {payBooking && (
        <div data-testid="kp-payment-modal" className="fixed inset-0 z-50 bg-slate-900/70 flex items-end sm:items-center justify-center p-3 sm:p-6">
          <div className="bg-white text-slate-900 rounded-2xl shadow-2xl w-full max-w-md max-h-[92vh] overflow-y-auto">
            <div className="p-5 border-b border-slate-200 flex items-start justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Payment link</div>
                <div className="font-bold text-slate-900 mt-0.5">{payBooking.client_name}</div>
                <div className="text-[12px] text-slate-500">{payBooking.id}</div>
              </div>
              <button data-testid="kp-payment-close" onClick={closePaymentPanel} className="rounded-full p-1.5 hover:bg-slate-100 text-slate-500"><XIcon size={18} /></button>
            </div>

            <div className="p-5 space-y-4">
              {/* Currency toggle */}
              <div>
                <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Currency</label>
                <div className="mt-1 flex gap-2">
                  <button
                    type="button"
                    data-testid="kp-payment-ccy-jmd"
                    onClick={() => onChangePayCurrency("JMD")}
                    className={`flex-1 rounded-xl py-2 text-sm font-semibold border-2 ${payCurrency === "JMD" ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-white text-slate-600"}`}
                  >JMD - Fygaro</button>
                  <button
                    type="button"
                    data-testid="kp-payment-ccy-usd"
                    onClick={() => onChangePayCurrency("USD")}
                    className={`flex-1 rounded-xl py-2 text-sm font-semibold border-2 ${payCurrency === "USD" ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-white text-slate-600"}`}
                  >USD - Stripe</button>
                </div>
              </div>

              {/* Quote breakdown */}
              {payQuote && (
                <div data-testid="kp-payment-quote" className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm">
                  <div className="flex justify-between"><span className="text-slate-600">Package cost</span><span className="font-mono tabular-nums">{payQuote.currency === "USD" ? `JMD ${parseFloat(payBooking.package_cost).toFixed(2)}` : `JMD ${payQuote.cost}`}</span></div>
                  {payCurrency === "USD" && (
                    <div className="flex justify-between text-[12px] mt-1"><span className="text-slate-500">FX rate (locked)</span><span className="font-mono">1 USD = 145 JMD</span></div>
                  )}
                  {payQuote.surcharge_applied && (
                    <div className="flex justify-between mt-1 text-[12px]"><span className="text-slate-600">Card processing fee</span><span className="font-mono tabular-nums">+ JMD {payQuote.surcharge}</span></div>
                  )}
                  {!payQuote.surcharge_applied && payCurrency === "JMD" && parseFloat(payBooking.package_cost) >= 850 && (
                    <div className="text-[11px] text-slate-500 mt-1 italic">No surcharge -- fee absorbed (cost ≥ J$850).</div>
                  )}
                  <div className="border-t border-slate-200 mt-2 pt-2 flex justify-between font-bold text-slate-900">
                    <span>Customer pays</span><span className="font-mono tabular-nums" data-testid="kp-payment-total">{payQuote.currency} {payQuote.total}</span>
                  </div>
                </div>
              )}

              {/* Action button -- create or resend link */}
              {!payResult?.pay_url || (payBooking.payment_currency && payBooking.payment_currency !== payCurrency) ? (
                <button
                  type="button"
                  data-testid="kp-payment-create"
                  onClick={submitPaymentLink}
                  disabled={paySubmitting || !payQuote}
                  className={`w-full rounded-xl py-3 font-bold text-base ${(!paySubmitting && payQuote) ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-slate-200 text-slate-500"}`}
                >
                  {paySubmitting ? "Creating link…" : `Generate ${payCurrency} payment link`}
                </button>
              ) : (
                <div className="space-y-2">
                  <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2 text-[12px] text-emerald-800">
                    <CheckCircle2 className="inline w-4 h-4 mr-1" /> Link ready. Share via the channels below.
                  </div>
                  <a
                    href={payResult.pay_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="kp-payment-pay-url"
                    className="block rounded-xl border border-slate-300 bg-slate-50 px-3 py-2 text-[12px] text-slate-700 truncate font-mono hover:bg-slate-100"
                  >{payResult.pay_url}</a>
                  <div className="grid grid-cols-3 gap-2">
                    <a
                      data-testid="kp-payment-send-email"
                      href={payBooking.client_email ? `mailto:${payBooking.client_email}?subject=${encodeURIComponent("Liantiao GO payment link")}&body=${encodeURIComponent(payResult.pay_url)}` : "#"}
                      onClick={(e) => { if (!payBooking.client_email) { e.preventDefault(); showToast("err", "No email on booking."); } }}
                      className={`rounded-xl py-2 text-[12px] font-semibold text-center ${payBooking.client_email ? "bg-slate-900 text-white hover:bg-slate-800" : "bg-slate-100 text-slate-400 cursor-not-allowed"}`}
                    ><Mail size={12} className="inline mr-1" />Email</a>
                    <a
                      data-testid="kp-payment-send-whatsapp"
                      href={payResult.channels?.whatsapp_url || "#"}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => { if (!payResult.channels?.whatsapp_url) { e.preventDefault(); showToast("err", "No phone on booking."); } }}
                      className={`rounded-xl py-2 text-[12px] font-semibold text-center ${payResult.channels?.whatsapp_url ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-slate-100 text-slate-400 cursor-not-allowed"}`}
                    ><MessageCircle size={12} className="inline mr-1" />WhatsApp</a>
                    <button
                      data-testid="kp-payment-copy"
                      type="button"
                      onClick={copyPayUrl}
                      className="rounded-xl py-2 text-[12px] font-semibold text-center bg-slate-700 text-white hover:bg-slate-800"
                    ><CopyIcon size={12} className="inline mr-1" />{payCopyOk ? "Copied!" : "Copy link"}</button>
                  </div>
                  <button
                    type="button"
                    data-testid="kp-payment-resend"
                    onClick={submitPaymentLink}
                    disabled={paySubmitting}
                    className="w-full rounded-xl py-2 text-[12px] font-semibold text-emerald-700 hover:underline"
                  >Generate a fresh link instead</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* R30 B2: Karishma -> Customer GPS share dialog */}
      {gpsBooking && (
        <Modal
          title={gpsBooking?._target === "destination" ? "Request destination location" : "Request pickup location"}
          onClose={closeGpsShare}
          testid="kp-modal-gps-share"
        >
          <div className="space-y-4 text-sm">
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
              <div className="font-semibold text-slate-900">{gpsBooking.client_name || "Customer"}</div>
              {gpsBooking.client_phone && (
                <div className="text-[12px] text-slate-600">{gpsBooking.client_phone}</div>
              )}
              <div className="text-[11px] text-slate-500 mt-1">
                {gpsBooking._inForm ? "New booking — coords will save with the booking." : `Booking ${gpsBooking.id}`}
              </div>
            </div>

            {/* R30 B2: live status — once customer submits, show captured coords */}
            {gpsShare?.location && (
              <>
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 flex items-center justify-between gap-2" data-testid="kp-gps-captured">
                  <div>
                    <div className="text-[12px] font-bold text-emerald-800 inline-flex items-center gap-1">
                      <CheckCircle2 size={13} /> {gpsBooking?._target === "destination" ? "Destination location captured" : "Pickup location captured"}
                    </div>
                    <div className="text-[11px] font-mono tabular-nums text-emerald-700">
                      {Number(gpsShare.location.lat).toFixed(6)}, {Number(gpsShare.location.lng).toFixed(6)}
                    </div>
                  </div>
                  <a
                    href={`https://www.google.com/maps?q=${gpsShare.location.lat},${gpsShare.location.lng}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 rounded-full bg-white border border-emerald-300 text-emerald-700 px-3 py-[3px] text-[11px] font-semibold hover:bg-emerald-100"
                  >
                    <Navigation size={11} /> Map
                  </a>
                </div>
                <button
                  type="button"
                  onClick={closeGpsShare}
                  data-testid="kp-gps-done"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 text-white px-3 py-2.5 text-sm font-semibold hover:bg-slate-800"
                >
                  <CheckCircle2 size={14} /> Done — back to booking
                </button>
              </>
            )}

            {gpsBusy && !gpsShare && (
              <div className="flex items-center justify-center py-4 text-slate-500" data-testid="kp-gps-loading">
                <Loader2 className="animate-spin mr-2" size={16} /> Creating link...
              </div>
            )}

            {gpsErr && (
              <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-rose-700 text-[13px]" data-testid="kp-gps-err">
                {gpsErr}
              </div>
            )}

            {gpsShare && !gpsShare.location && (
              <>
                <div>
                  <div className="text-[11px] uppercase font-semibold text-slate-500 mb-1">Share link (expires in 30 min)</div>
                  <div className="rounded-lg bg-white border border-slate-300 px-3 py-2 break-all text-[12px] font-mono text-slate-800" data-testid="kp-gps-link">
                    {gpsShare.share_url}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={copyGpsLink}
                    data-testid="kp-gps-copy"
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                  >
                    <CopyIcon size={14} /> {gpsCopied ? "Copied!" : "Copy link"}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setGpsEmailOpen(true); setGpsErr(""); }}
                    data-testid="kp-gps-email-open"
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                  >
                    <Mail size={14} /> Email
                  </button>
                  <a
                    href={waLink || "#"}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="kp-gps-whatsapp"
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-100"
                  >
                    <MessageCircle size={14} /> WhatsApp
                  </a>
                  <a
                    href={smsLink || "#"}
                    data-testid="kp-gps-sms"
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                  >
                    <MessageCircle size={14} /> SMS
                  </a>
                </div>

                {gpsEmailSent && (
                  <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-emerald-700 text-[13px]" data-testid="kp-gps-email-sent">
                    Email queued via Resend.
                  </div>
                )}

                {gpsEmailOpen && (
                  <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 space-y-2">
                    <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">Customer email</label>
                    <input
                      data-testid="kp-gps-email-input"
                      type="email"
                      value={gpsEmailTo}
                      onChange={(e) => setGpsEmailTo(e.target.value)}
                      placeholder="customer@example.com"
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                    />
                    <button
                      type="button"
                      onClick={sendGpsEmail}
                      disabled={gpsBusy}
                      data-testid="kp-gps-email-send"
                      className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 text-white px-3 py-2.5 text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60"
                    >
                      {gpsBusy ? <><Loader2 className="animate-spin" size={14} /> Sending...</> : <><Mail size={14} /> Send email</>}
                    </button>
                  </div>
                )}

                <p className="text-[11px] text-slate-500">
                  The customer just taps "Share my location" on the page - no app install needed. Once they share, the coordinates appear on this booking and the assigned driver sees them.
                </p>
              </>
            )}
          </div>
        </Modal>
      )}

      {/* Toast */}
      {toast && (
        <div data-testid="kp-toast" className={`fixed bottom-6 left-1/2 -translate-x-1/2 rounded-xl shadow-lg px-4 py-2.5 text-sm font-semibold ${
          toast.kind === "ok" ? "bg-emerald-600 text-white" : "bg-rose-600 text-white"
        }`}>
          <span className="inline-flex items-center gap-2">
            {toast.kind === "ok" ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            {toast.text}
          </span>
        </div>
      )}
    </div>
  );
}

/* ── Tiny components ── */

function SmartAddressInput({ testid, label, value, onChange, lat, lng, onPick, onClear, suggestions, placeholder }) {
  const [results, setResults] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const verified = lat != null && lng != null;
  const datalistId = `dl-${testid}`;

  // Debounced geocode lookup
  React.useEffect(() => {
    const q = (value || "").trim();
    if (q.length < 3) { setResults([]); return undefined; }
    let stopped = false;
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await api.get(`/karishma/geocode`, { params: { q } });
        if (!stopped) {
          setResults(r.data?.results || []);
          setOpen(true);
        }
      } catch { if (!stopped) setResults([]); }
      finally { if (!stopped) setBusy(false); }
    }, 700);
    return () => { stopped = true; clearTimeout(t); };
  }, [value]);

  const previewMapSrc = verified
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${lng - 0.005}%2C${lat - 0.005}%2C${lng + 0.005}%2C${lat + 0.005}&layer=mapnik&marker=${lat}%2C${lng}`
    : null;

  return (
    <div className="space-y-1.5">
      <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-600 flex items-center gap-2">
        <span>{label}</span>
        {verified ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 px-2 py-[1px] text-[10px] font-bold" data-testid={`${testid}-verified`}>
            <CheckCircle2 size={10} /> Verified GPS
          </span>
        ) : busy ? (
          <span className="inline-flex items-center gap-1 text-[10px] text-slate-500"><Loader2 size={10} className="animate-spin" /> Searching</span>
        ) : null}
      </label>
      <div className="relative">
        <datalist id={datalistId}>
          {(suggestions || []).map((a) => <option key={a} value={a} />)}
        </datalist>
        <input
          data-testid={testid}
          type="text"
          list={datalistId}
          autoComplete="off"
          value={value}
          onChange={(e) => { onChange(e.target.value); if (verified && onClear) onClear(); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="w-full rounded-xl border border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 px-3 py-2.5 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
        />
        {open && results.length > 0 && (
          <ul data-testid={`${testid}-results`} className="absolute z-20 left-0 right-0 mt-1 rounded-xl border border-slate-200 bg-white shadow-lg max-h-60 overflow-y-auto">
            {results.map((r) => (
              <li key={r.place_id || `${r.lat},${r.lng}`}>
                <button
                  type="button"
                  onClick={() => { onPick(r); setOpen(false); setResults([]); }}
                  data-testid={`${testid}-pick-${r.place_id || `${r.lat}-${r.lng}`}`}
                  className="w-full text-left px-3 py-2 hover:bg-emerald-50 text-[13px] text-slate-700"
                >
                  <div className="flex items-start gap-2">
                    <MapPin size={12} className="text-emerald-600 mt-[3px]" />
                    <div className="flex-1">
                      <div className="font-semibold text-slate-900 line-clamp-1">{r.display_name}</div>
                      <div className="text-[10px] text-slate-500 font-mono tabular-nums">{Number(r.lat).toFixed(5)}, {Number(r.lng).toFixed(5)}</div>
                    </div>
                  </div>
                </button>
              </li>
            ))}
            <li className="px-3 py-1.5 text-[10px] text-slate-400 border-t border-slate-100">Powered by OpenStreetMap</li>
          </ul>
        )}
      </div>
      {previewMapSrc && (
        <div className="rounded-xl overflow-hidden border border-emerald-200" data-testid={`${testid}-map`}>
          <iframe
            title={`${label} map preview`}
            src={previewMapSrc}
            className="w-full"
            style={{ height: 180, border: 0 }}
            loading="lazy"
          />
          <div className="flex items-center justify-between gap-2 bg-emerald-50 px-3 py-1.5 text-[11px]">
            <span className="font-mono tabular-nums text-emerald-700">{Number(lat).toFixed(6)}, {Number(lng).toFixed(6)}</span>
            <button type="button" onClick={onClear} className="text-rose-600 hover:underline font-semibold" data-testid={`${testid}-map-clear`}>Clear pin</button>
          </div>
        </div>
      )}
    </div>
  );
}

function GpsRequestRow({ title, hint, lat, lng, testidPrefix, onRequest, onClear }) {
  const captured = lat != null && lng != null;
  return (
    <div data-testid={`${testidPrefix}-block`} className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-[12px] font-bold text-slate-900 flex items-center gap-1">
            <MapPin size={13} className="text-emerald-700" />
            {title}
          </div>
          <div className="text-[11px] text-slate-600">
            {captured ? "Captured — will save with this booking." : hint}
          </div>
        </div>
        {captured ? (
          <a
            href={`https://www.google.com/maps?q=${lat},${lng}`}
            target="_blank"
            rel="noreferrer"
            data-testid={`${testidPrefix}-open-maps`}
            className="inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-3 py-[3px] text-[11px] font-semibold hover:bg-emerald-700"
          >
            <Navigation size={11} /> Open in Maps
          </a>
        ) : (
          <button
            type="button"
            onClick={onRequest}
            data-testid={`${testidPrefix}-request`}
            className="inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-3 py-[5px] text-[12px] font-semibold hover:bg-emerald-700"
          >
            <MapPin size={12} /> Request location
          </button>
        )}
      </div>
      {captured && (
        <div className="flex items-center justify-between gap-2 text-[11px]">
          <span className="font-mono tabular-nums text-emerald-700" data-testid={`${testidPrefix}-coords`}>
            {Number(lat).toFixed(6)}, {Number(lng).toFixed(6)}
          </span>
          <button
            type="button"
            onClick={onClear}
            data-testid={`${testidPrefix}-clear`}
            className="text-rose-600 hover:underline font-semibold"
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, testid, emerald, amber }) {
  const tone = emerald ? "text-emerald-700" : amber ? "text-amber-700" : "text-slate-900";
  return (
    <div data-testid={testid} className="rounded-2xl bg-white border border-slate-200 p-3 shadow-sm">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`text-base font-bold mt-0.5 tabular-nums ${tone}`}>{value}</div>
    </div>
  );
}

function ActionBtn({ onClick, icon: Icon, label, testid, emerald }) {
  const cls = emerald
    ? "bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600"
    : "bg-white hover:bg-slate-50 text-slate-800 border-slate-300";
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-2 rounded-xl border px-4 py-3 font-bold text-sm shadow-sm ${cls}`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

function Modal({ title, onClose, testid, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div data-testid={testid} className="bg-white w-full sm:max-w-md max-h-[90vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl shadow-2xl p-5 sm:p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900 text-2xl leading-none">×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function CustomerSuggestPanel({ busy, query, results, onPick }) {
  const list = results || [];
  return (
    <div
      data-testid="kp-customer-suggest-panel"
      className="absolute z-30 left-0 right-0 mt-1 rounded-xl border border-slate-200 bg-white text-slate-900 shadow-lg overflow-hidden"
      onMouseDown={(e) => e.preventDefault()}
    >
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-100 bg-slate-50">
        <span className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Saved customers</span>
        {busy && <Loader2 size={12} className="animate-spin text-slate-400" />}
      </div>
      <ul className="max-h-64 overflow-y-auto">
        {list.length === 0 && !busy && (
          <li className="px-3 py-2 text-[12px] text-slate-500" data-testid="kp-customer-suggest-empty">
            No saved customers match &ldquo;{query}&rdquo;.
          </li>
        )}
        {list.map((c) => (
          <li key={c.id}>
            <button
              type="button"
              onClick={() => onPick(c)}
              data-testid={`kp-customer-suggest-pick-${c.id}`}
              className="w-full text-left px-3 py-2 hover:bg-emerald-50"
            >
              <div className="text-[13px] font-semibold text-slate-900">{c.name || "(unnamed)"}</div>
              <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
                <span>{c.phone || "no phone"}</span>
                {c.email && (<><span>·</span><span className="text-slate-600">{c.email}</span></>)}
                <span>·</span>
                <span>{(c.addresses || []).length} address{(c.addresses || []).length === 1 ? "" : "es"}</span>
                <span>·</span>
                <span>{c.booking_count || 0} bookings</span>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Field({ testid, label, value, onChange, type = "text", ...rest }) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-base text-slate-900 placeholder:text-slate-400 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
        {...rest}
      />
    </div>
  );
}

function Textarea({ testid, label, value, onChange }) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">{label}</label>
      <textarea
        data-testid={testid}
        rows={2}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
      />
    </div>
  );
}

function Select({ testid, label, value, onChange, options, placeholder }) {
  // Accepts options as array of strings OR {value,label}
  const norm = (options || []).map((o) =>
    typeof o === "string" ? { value: o, label: o } : o
  );
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">{label}</label>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-base text-slate-900 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
      >
        <option value="">{placeholder}</option>
        {norm.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

function SubmitRow({ submitting, label, testid }) {
  return (
    <button
      type="submit"
      data-testid={testid}
      disabled={submitting}
      className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white font-bold py-3 text-base inline-flex items-center justify-center gap-2"
    >
      {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
      {submitting ? "Saving…" : label}
    </button>
  );
}
